import {
  DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { useMemo } from "react";

// Utility function to strip date and time information from strings
const stripDateTimeInfo = (text: string): string => {
  if (!text) return text;

  return (
    text
      // Remove times with AM/PM first (more specific patterns first)
      .replace(/\b\d{1,2}:\d{2}:\d{2}\s*[APap][Mm]\b/g, "")
      .replace(/\b\d{1,2}:\d{2}\s*[APap][Mm]\b/g, "")
      // Remove dates in format: YYYY-MM-DD, YYYY/MM/DD, MM/DD/YYYY, DD/MM/YYYY
      .replace(/\b\d{4}[-/]\d{1,2}[-/]\d{1,2}\b/g, "")
      .replace(/\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/g, "")
      // Remove times in format: HH:MM:SS, H:MM:SS (24-hour format)
      .replace(/\b\d{1,2}:\d{2}:\d{2}\b/g, "")
      // Remove any extra whitespace that might be left
      .replace(/\s+/g, " ")
      .trim()
  );
};

export const useJoinedErrors = (): DevErrorJson[] => {
  const storyboardIdToErrors = useDevServerStore(
    (store) => store.storyboardIdToErrors,
  );

  const terminalErrors = useDevServerStore((state) => state.terminalErrors);

  return useMemo(() => {
    const storyboardIdToErrorsEntries = Object.entries(
      storyboardIdToErrors,
    ) as [string | null, DevErrorJson[]][];

    // Group by equal message, filename and stack (with date/time stripped for comparison)
    const groupedErrors = storyboardIdToErrorsEntries
      .concat(terminalErrors.map((error) => [null, [error]]))
      .reduce((acc, [storyboardId, errors]) => {
        errors?.forEach((error) => {
          if (!error) {
            return;
          }

          // Strip date/time info for comparison purposes
          const strippedMessage = stripDateTimeInfo(error.message || "");
          const strippedFilename = stripDateTimeInfo(error.filename || "");
          const strippedStack = stripDateTimeInfo(error.stack || "");

          // Find existing error with same stripped content
          const existingErrorKey = Object.keys(acc).find((key) => {
            const existing = acc[key];
            return (
              existing.message === strippedMessage &&
              existing.filename === strippedFilename &&
              existing.stack === strippedStack
            );
          });

          if (existingErrorKey) {
            acc[existingErrorKey].count++;
            if (storyboardId) {
              acc[existingErrorKey].storyboards.push(storyboardId);
            }
          } else {
            acc[error.message] = {
              ...error,
              message: strippedMessage,
              filename: strippedFilename,
              stack: strippedStack,
              count: 1,
              storyboards: storyboardId ? [storyboardId] : [],
            };
          }
        });
        return acc;
      }, {});

    return Object.values(groupedErrors);
  }, [storyboardIdToErrors, terminalErrors]);
};

const MAX_ERRORS_IN_MESSAGE = 10;

export const getAiMessageFromErrors = (errors: DevErrorJson[]) => {
  const errorsToInclude = errors.slice(0, MAX_ERRORS_IN_MESSAGE);

  return {
    role: "user",
    content:
      "\n<dev-errors>\n" +
      JSON.stringify(errorsToInclude) +
      "\n</dev-errors>\n",
  };
};

// Reverse of getAiMessageFromErrors
export const getErrorsFromAIMessageContent = (content: string) => {
  const startTag = "<dev-errors>";
  const endTag = "</dev-errors>";

  const jsonContent = content
    .replace(new RegExp(`${startTag}\\n`), "")
    .replace(new RegExp(`\\n${endTag}`), "");

  try {
    return JSON.parse(jsonContent) as DevErrorJson[];
  } catch (e) {
    return [];
  }
};
